import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Space, Statistic, Table, Tag, Button, Input, Select, message, Modal, Form } from 'antd';
import api from '../api/http';
import dayjs from 'dayjs';
import AuthedImage from '../components/AuthedImage';
import { openWithAuth } from '../utils/authedResource';

const STATUS_OPTIONS = [
  { label: '全部', value: '' },
  { label: '新建', value: 'NEW' },
  { label: '处理中', value: 'IN_PROGRESS' },
  { label: '已处理', value: 'RESOLVED' },
  { label: '忽略', value: 'IGNORED' }
];

const statusLabel = (s) => {
  const v = String(s || '').toUpperCase();
  if (v === 'NEW') return '新建';
  if (v === 'IN_PROGRESS') return '处理中';
  if (v === 'RESOLVED') return '已处理';
  if (v === 'IGNORED') return '忽略';
  return v || '-';
};

const statusColor = (s) => {
  const v = String(s || '').toUpperCase();
  if (v === 'NEW') return 'blue';
  if (v === 'IN_PROGRESS') return 'gold';
  if (v === 'RESOLVED') return 'green';
  if (v === 'IGNORED') return 'default';
  return 'default';
};

export default function FeedbackAdminPage() {
  const [stats, setStats] = useState({ total: 0, today: 0, byStatus: { NEW: 0, IN_PROGRESS: 0, RESOLVED: 0, IGNORED: 0 } });
  const [loadingStats, setLoadingStats] = useState(false);

  const [query, setQuery] = useState({ keyword: '', status: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [loadingList, setLoadingList] = useState(false);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);

  const [noteModal, setNoteModal] = useState({ open: false, record: null });
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteForm] = Form.useForm();

  const [imageModal, setImageModal] = useState({ open: false, record: null });

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await api.get('/feedback/stats');
      setStats(res.data || { total: 0, today: 0, byStatus: { NEW: 0, IN_PROGRESS: 0, RESOLVED: 0, IGNORED: 0 } });
    } catch (e) {
      console.error('fetch feedback stats failed', e);
      message.error('获取反馈统计失败');
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const fetchList = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? page;
    const nextPageSize = opts.pageSize ?? pageSize;
    const nextQuery = opts.query ?? query;

    setLoadingList(true);
    try {
      const res = await api.get('/feedback/submissions', {
        params: {
          page: nextPage,
          pageSize: nextPageSize,
          status: nextQuery.status || undefined,
          keyword: nextQuery.keyword || undefined
        }
      });
      setList(Array.isArray(res.data?.list) ? res.data.list : []);
      setTotal(Number(res.data?.pagination?.total || 0));
    } catch (e) {
      console.error('fetch feedback list failed', e);
      message.error('获取反馈列表失败');
    } finally {
      setLoadingList(false);
    }
  }, [page, pageSize, query]);

  useEffect(() => {
    fetchStats();
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = async () => {
    await Promise.all([fetchStats(), fetchList()]);
  };

  const updateStatus = useCallback(async (record, status, pmNote) => {
    try {
      await api.patch(`/feedback/submissions/${record.id}/status`, {
        status,
        pm_note: pmNote !== undefined ? pmNote : record.pm_note
      });
      message.success('状态已更新');
      await Promise.all([fetchStats(), fetchList()]);
    } catch (e) {
      console.error('update feedback status failed', e);
      message.error('更新状态失败');
    }
  }, [fetchList, fetchStats]);

  const openNote = useCallback((record) => {
    setNoteModal({ open: true, record });
    noteForm.setFieldsValue({ pm_note: record?.pm_note || '' });
  }, [noteForm]);

  const saveNote = async () => {
    const record = noteModal.record;
    if (!record) return;

    setNoteSaving(true);
    try {
      const vals = await noteForm.validateFields();
      await updateStatus(record, String(record.status || 'NEW').toUpperCase() || 'NEW', vals.pm_note);
      setNoteModal({ open: false, record: null });
    } finally {
      setNoteSaving(false);
    }
  };

  const columns = useMemo(() => {
    return [
      { title: 'ID', dataIndex: 'id', width: 80 },
      {
        title: '状态',
        dataIndex: 'status',
        width: 110,
        render: (v) => <Tag color={statusColor(v)}>{statusLabel(v)}</Tag>
      },
      {
        title: '图片',
        dataIndex: 'images',
        width: 150,
        render: (_, record) => {
          const imgs = Array.isArray(record?.images) ? record.images.filter((it) => it && it.url) : [];
          if (!imgs.length) return '-';
          const show = imgs.slice(0, 3);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {show.map((it, idx) => (
                <div
                  key={`${record.id}-${idx}`}
                  style={{ width: 36, height: 36, borderRadius: 4, overflow: 'hidden', background: '#f5f5f5' }}
                  title={it.fileName || it.objectName || ''}
                >
                  <AuthedImage
                    src={it.url}
                    alt={it.fileName || it.objectName || 'feedback'}
                    style={{ width: 36, height: 36, objectFit: 'cover' }}
                  />
                </div>
              ))}
              <Button size="small" onClick={() => setImageModal({ open: true, record })}>
                查看({imgs.length})
              </Button>
            </div>
          );
        }
      },
      {
        title: '反馈内容',
        dataIndex: 'content',
        width: 560,
        render: (v) => (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {String(v || '').slice(0, 500) || '-'}
          </div>
        )
      },
      { title: '联系方式', dataIndex: 'contact', width: 160, render: (v) => v || '-' },
      { title: '页面路径', dataIndex: 'page_path', width: 180, render: (v) => v || '-' },
      {
        title: '提交时间',
        dataIndex: 'created_at',
        width: 170,
        render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-')
      },
      {
        title: '处理人',
        dataIndex: 'handled_by_name',
        width: 120,
        render: (v) => v || '-'
      },
      {
        title: '处理时间',
        dataIndex: 'handled_at',
        width: 170,
        render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-')
      },
      {
        title: '操作',
        key: 'actions',
        width: 260,
        fixed: 'right',
        render: (_, record) => (
          <Space wrap>
            <Button size="small" onClick={() => updateStatus(record, 'IN_PROGRESS')}>处理中</Button>
            <Button size="small" type="primary" onClick={() => updateStatus(record, 'RESOLVED')}>已处理</Button>
            <Button size="small" danger onClick={() => updateStatus(record, 'IGNORED')}>忽略</Button>
            <Button size="small" onClick={() => openNote(record)}>备注</Button>
          </Space>
        )
      }
    ];
  }, [openNote, updateStatus]);

  return (
    <div style={{ padding: 16 }}>
      <Card title="意见反馈（项目经理）" extra={<Button onClick={refreshAll} loading={loadingStats || loadingList}>刷新</Button>}>
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={6} md={6} lg={4}>
            <Card size="small" loading={loadingStats}>
              <Statistic title="总数" value={stats.total || 0} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <Card size="small" loading={loadingStats}>
              <Statistic title="今日新增" value={stats.today || 0} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <Card size="small" loading={loadingStats}>
              <Statistic title="新建" value={stats.byStatus?.NEW || 0} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <Card size="small" loading={loadingStats}>
              <Statistic title="处理中" value={stats.byStatus?.IN_PROGRESS || 0} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <Card size="small" loading={loadingStats}>
              <Statistic title="已处理" value={stats.byStatus?.RESOLVED || 0} />
            </Card>
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <Card size="small" loading={loadingStats}>
              <Statistic title="忽略" value={stats.byStatus?.IGNORED || 0} />
            </Card>
          </Col>
        </Row>

        <Space wrap style={{ marginBottom: 12 }}>
          <Input
            placeholder="关键字（内容/联系方式/页面路径）"
            value={query.keyword}
            onChange={(e) => setQuery((q) => ({ ...q, keyword: e.target.value }))}
            style={{ width: 260 }}
            allowClear
          />
          <Select
            options={STATUS_OPTIONS}
            value={query.status}
            onChange={(v) => setQuery((q) => ({ ...q, status: v }))}
            style={{ width: 160 }}
          />
          <Button
            type="primary"
            onClick={() => {
              setPage(1);
              fetchList({ page: 1, query });
            }}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              const next = { keyword: '', status: '' };
              setQuery(next);
              setPage(1);
              fetchList({ page: 1, query: next });
            }}
          >
            重置
          </Button>
        </Space>

        <Table
          rowKey="id"
          loading={loadingList}
          dataSource={list}
          columns={columns}
          tableLayout="fixed"
          scroll={{ x: 1700 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
              fetchList({ page: p, pageSize: ps });
            }
          }}
          size="small"
        />

        <Modal
          title={`反馈图片（ID: ${imageModal.record?.id ?? '-'}）`}
          open={imageModal.open}
          onCancel={() => setImageModal({ open: false, record: null })}
          footer={null}
          width={860}
        >
          {(() => {
            const imgs = Array.isArray(imageModal.record?.images) ? imageModal.record.images.filter((it) => it && it.url) : [];
            if (!imgs.length) return <div style={{ color: '#666' }}>无图片</div>;
            return (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {imgs.map((it, idx) => (
                  <div key={`${imageModal.record?.id}-${idx}`} style={{ width: 180 }}>
                    <div style={{ width: 180, height: 180, borderRadius: 6, overflow: 'hidden', background: '#f5f5f5' }}>
                      <AuthedImage
                        src={it.url}
                        alt={it.fileName || it.objectName || 'feedback'}
                        style={{ width: 180, height: 180, objectFit: 'cover' }}
                      />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#666', wordBreak: 'break-all' }}>
                      {it.fileName || it.objectName || ''}
                    </div>
                    <Button size="small" type="link" onClick={() => openWithAuth(it.url)} style={{ paddingLeft: 0 }}>
                      打开
                    </Button>
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal>
      </Card>

      <Modal
        title={`备注（ID: ${noteModal.record?.id || ''}）`}
        open={noteModal.open}
        onCancel={() => setNoteModal({ open: false, record: null })}
        onOk={saveNote}
        confirmLoading={noteSaving}
        destroyOnClose
      >
        <Form form={noteForm} layout="vertical">
          <Form.Item name="pm_note" label="处理备注" rules={[{ max: 255, message: '最多255字符' }]}>
            <Input.TextArea rows={4} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
